import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import api, { formatApiError } from "../../lib/api";
import { Upload, AlertTriangle, Loader2, ArrowRight, Plus, Minus, GitMerge, Info, CheckCircle2 } from "lucide-react";

/**
 * RevisionDiffDialog — "Auto-check disegni": compare a freshly uploaded
 * revision of the technical drawing against the requirements stored on
 * the current quote and surface the differences (material, treatments,
 * tolerances, threads, GD&T, surface finish, notes, revision header).
 *
 * All copy is keyed under the ``revisionDiff`` i18n namespace. Italian
 * remains the source language; other locales fall back via i18next.
 */

const SEV_BADGE_CLS = {
  alert: "bg-red-500/10 border-red-500/40 text-red-300",
  warn:  "bg-amber-500/10 border-amber-500/40 text-amber-300",
  info:  "bg-slate-700/30 border-slate-600 text-slate-300",
};

const KIND_ICON = {
  added: { icon: Plus, color: "text-emerald-400" },
  removed: { icon: Minus, color: "text-rose-400" },
  changed: { icon: ArrowRight, color: "text-blue-400" },
};

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = (e) => resolve(String(e.target.result).split(",")[1]);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function detectFormat(file) {
  const n = (file?.name || "").toLowerCase();
  if (n.endsWith(".step") || n.endsWith(".stp")) return "step";
  if (n.endsWith(".iges") || n.endsWith(".igs")) return "iges";
  if (n.endsWith(".pdf")) return "pdf";
  if (n.endsWith(".dxf")) return "dxf";
  if (n.endsWith(".webp")) return "webp";
  if (n.endsWith(".jpg") || n.endsWith(".jpeg")) return "jpeg";
  return "png";
}

function DiffRow({ entry, t }) {
  const KindIcon = KIND_ICON[entry.kind]?.icon || Info;
  const kindColor = KIND_ICON[entry.kind]?.color || "text-slate-400";
  const kindLabel = t(`revisionDiff.kind.${entry.kind}`, { defaultValue: entry.kind });
  const sevKey = entry.severity || "info";
  const sevLabel = t(`revisionDiff.severity.${sevKey}`, { defaultValue: sevKey });
  const sevCls = SEV_BADGE_CLS[sevKey] || SEV_BADGE_CLS.info;
  const fieldLabel = t(`revisionDiff.fields.${entry.field}`, { defaultValue: entry.field });
  return (
    <li
      className="flex items-start gap-3 px-3 py-2.5 rounded border border-slate-700/60 bg-slate-900/30"
      data-testid={`diff-row-${entry.field}-${entry.kind}`}
    >
      <KindIcon size={16} className={`mt-0.5 ${kindColor} flex-shrink-0`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[11px] uppercase tracking-wider text-slate-400 font-mono">{kindLabel}</span>
          <span className="text-slate-100 font-medium text-sm">{fieldLabel}</span>
          <span className={`ml-auto text-[10px] uppercase px-2 py-0.5 rounded border ${sevCls}`}>
            {sevLabel}
          </span>
        </div>
        <div className="text-sm font-mono-data text-slate-300">
          {entry.kind === "added" && <span className="text-emerald-300">+ {String(entry.new)}</span>}
          {entry.kind === "removed" && <span className="text-rose-300 line-through opacity-70">{String(entry.old)}</span>}
          {entry.kind === "changed" && (
            <>
              <span className="text-rose-300 line-through opacity-70">{String(entry.old)}</span>
              <ArrowRight size={12} className="inline mx-2 text-slate-500" />
              <span className="text-emerald-300">{String(entry.new)}</span>
            </>
          )}
        </div>
        {entry.extras?.old_family && entry.extras?.new_family && entry.extras.old_family !== entry.extras.new_family && (
          <div className="text-[11px] text-amber-300 mt-1">
            {t("revisionDiff.familyChange", {
              old: entry.extras.old_family,
              new: entry.extras.new_family,
              defaultValue: `Famiglia: ${entry.extras.old_family} → ${entry.extras.new_family}`,
            })}
          </div>
        )}
      </div>
    </li>
  );
}

export default function RevisionDiffDialog({ open, onOpenChange, quoteId }) {
  const { t } = useTranslation();
  const fileRef = useRef(null);
  const [status, setStatus] = useState("idle"); // idle | uploading | ready | error
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const reset = () => {
    setStatus("idle");
    setError("");
    setResult(null);
  };

  const handleFile = async (file) => {
    if (!file) return;
    reset();
    setStatus("uploading");
    try {
      const fmt = detectFormat(file);
      const b64 = await fileToBase64(file);
      const { data } = await api.post(`/quotes/${quoteId}/check-revision`, {
        image_base64: b64,
        cad_format: fmt,
      });
      setResult(data);
      setStatus("ready");
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Structured backend errors carry a stable {code, message} pair — translate
      // by code so all locales see a clean message. Falls back to raw message
      // (string detail) when the backend hasn't been updated.
      let msg;
      if (detail && typeof detail === "object" && detail.code) {
        msg = t(`revisionDiff.errors.${detail.code}`, { defaultValue: detail.message || "" });
      } else {
        msg = formatApiError(detail) || err.message || "Errore";
      }
      setError(msg);
      setStatus("error");
    }
  };

  const onClose = (next) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const diff = result?.diff;
  const summary = diff?.summary;
  const headers = diff?.headers;
  const totalDiffs = summary ? summary.changed + summary.added + summary.removed : 0;
  const sevTone =
    summary?.severity === "alert" ? "border-red-500/40"
      : summary?.severity === "warn" ? "border-amber-500/40"
      : "border-slate-700";

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto bg-slate-900 border-slate-700" data-testid="revision-diff-dialog">
        <DialogHeader>
          <DialogTitle className="text-slate-100 flex items-center gap-2">
            <GitMerge size={18} className="text-blue-400" />
            {t("revisionDiff.title", { defaultValue: "Auto-check revisione disegno" })}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {t("revisionDiff.description", { defaultValue: "Carica una nuova revisione del disegno tecnico — l'AI evidenzierà le differenze rispetto alla versione attuale del preventivo." })}
          </DialogDescription>
        </DialogHeader>

        {status === "idle" && (
          <div className="py-8">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="w-full px-6 py-12 border-2 border-dashed border-slate-700 hover:border-blue-500 hover:bg-blue-500/5 rounded-lg flex flex-col items-center gap-3 transition-colors"
              data-testid="revision-upload-btn"
            >
              <Upload size={28} className="text-slate-500" />
              <div className="text-slate-300 font-medium">
                {t("revisionDiff.uploadButton", { defaultValue: "Carica nuova revisione del disegno" })}
              </div>
              <div className="text-xs text-slate-500">
                {t("revisionDiff.acceptedFormats", { defaultValue: "Accetta: PDF, DXF, PNG, JPG, STEP, IGES" })}
              </div>
            </button>
            <input
              ref={fileRef}
              type="file"
              accept=".png,.jpg,.jpeg,.webp,.pdf,.dxf,.step,.stp,.iges,.igs"
              onChange={(e) => handleFile(e.target.files[0])}
              className="hidden"
              data-testid="revision-file-input"
            />
          </div>
        )}

        {status === "uploading" && (
          <div className="py-12 flex flex-col items-center gap-4">
            <Loader2 size={32} className="animate-spin text-blue-400" />
            <div className="text-slate-300 text-sm">
              {t("revisionDiff.loading", { defaultValue: "Lettura del disegno e confronto in corso…" })}
            </div>
            <div className="text-xs text-slate-500">
              {t("revisionDiff.loadingHint", { defaultValue: "L'AI sta analizzando il nuovo disegno e confrontandolo col preventivo." })}
            </div>
          </div>
        )}

        {status === "error" && (
          <div className="py-8 flex flex-col items-center gap-3" data-testid="revision-error">
            <AlertTriangle size={28} className="text-amber-400" />
            <div className="text-amber-300 font-semibold">
              {t("revisionDiff.errorTitle", { defaultValue: "Confronto fallito" })}
            </div>
            <div className="text-xs text-slate-400 max-w-md text-center">{error}</div>
            <button
              type="button"
              onClick={reset}
              className="mt-2 px-3 py-1.5 rounded border border-slate-600 text-xs text-slate-300 hover:border-slate-400"
            >
              {t("revisionDiff.retry", { defaultValue: "Riprova" })}
            </button>
          </div>
        )}

        {status === "ready" && diff && (
          <div className="space-y-4" data-testid="revision-diff-result">
            <div className={`rounded-lg border ${sevTone} p-4`}>
              <div className="flex items-center justify-between mb-2">
                <div className="font-display font-bold text-slate-100">
                  {totalDiffs === 0
                    ? t("revisionDiff.noDifferences", { defaultValue: "Nessuna differenza rilevata" })
                    : t("revisionDiff.differencesDetected", { count: totalDiffs, defaultValue: `${totalDiffs} differenze rilevate` })}
                </div>
                {summary.severity === "alert" && (
                  <span className="text-xs px-2 py-0.5 rounded border border-red-500/40 bg-red-500/10 text-red-300 font-mono">
                    {t("revisionDiff.critical", { defaultValue: "REVISIONE CRITICA" })}
                  </span>
                )}
                {summary.severity === "warn" && (
                  <span className="text-xs px-2 py-0.5 rounded border border-amber-500/40 bg-amber-500/10 text-amber-300 font-mono">
                    {t("revisionDiff.review", { defaultValue: "DA RIVEDERE" })}
                  </span>
                )}
                {summary.severity === "info" && diff.diffs.length > 0 && (
                  <span className="text-xs px-2 py-0.5 rounded border border-slate-600 text-slate-300 font-mono">
                    {t("revisionDiff.minor", { defaultValue: "Modifiche minori" })}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-3 gap-4 mt-3 text-sm">
                <div className="flex items-center gap-2 text-blue-300">
                  <ArrowRight size={14}/> {t("revisionDiff.changed", { count: summary.changed, defaultValue: `${summary.changed} modificati` })}
                </div>
                <div className="flex items-center gap-2 text-emerald-300">
                  <Plus size={14}/> {t("revisionDiff.added", { count: summary.added, defaultValue: `${summary.added} aggiunti` })}
                </div>
                <div className="flex items-center gap-2 text-rose-300">
                  <Minus size={14}/> {t("revisionDiff.removed", { count: summary.removed, defaultValue: `${summary.removed} rimossi` })}
                </div>
              </div>
              {headers && (headers.old_revision || headers.new_revision) && (
                <div className="mt-3 pt-3 border-t border-slate-700/60 text-xs font-mono-data text-slate-400 flex items-center gap-2 flex-wrap">
                  <span className="text-slate-500">{t("revisionDiff.from", { defaultValue: "Da" })}</span>
                  <span className="text-slate-200">{headers.old_revision || "—"}</span>
                  <ArrowRight size={12} className="text-slate-500"/>
                  <span className="text-slate-200">{headers.new_revision || "—"}</span>
                  {headers.old_drawing_number && (
                    <span className="ml-auto text-slate-500">
                      {t("revisionDiff.drawing", { number: headers.old_drawing_number, defaultValue: `Disegno ${headers.old_drawing_number}` })}
                    </span>
                  )}
                </div>
              )}
            </div>

            {diff.diffs.length === 0 ? (
              <div className="flex items-center gap-2 text-emerald-300 px-4 py-3 bg-emerald-500/10 border border-emerald-500/40 rounded">
                <CheckCircle2 size={16}/>
                <span className="text-sm">
                  {t("revisionDiff.noChangesNeeded", { defaultValue: "Il disegno appena caricato è identico al preventivo attuale. Non servono modifiche." })}
                </span>
              </div>
            ) : (
              <ul className="space-y-2">
                {diff.diffs.map((entry, i) => (
                  <DiffRow key={`diff-${i}`} entry={entry} t={t} />
                ))}
              </ul>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t border-slate-800">
              <button
                type="button"
                onClick={reset}
                className="px-3 py-1.5 text-sm rounded border border-slate-700 text-slate-300 hover:border-slate-500"
                data-testid="revision-recheck-btn"
              >
                {t("revisionDiff.uploadAnother", { defaultValue: "Carica altro disegno" })}
              </button>
              <button
                type="button"
                onClick={() => onClose(false)}
                className="px-3 py-1.5 text-sm rounded bg-blue-600 hover:bg-blue-500 text-white"
                data-testid="revision-close-btn"
              >
                {t("revisionDiff.close", { defaultValue: "Chiudi" })}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
